import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Guid, isNil, Nillable, valueOrDefault } from '@web/utils';
import {
  OrderStatusPipe,
  PublicCheckoutOrderDetails,
  PublicOrderDataSourceService,
} from '@web/shared/module/shop';
import { interval, Subject, takeUntil, takeWhile } from 'rxjs';
import {
  AlertsComponent,
  AlertsService,
  CurrencyComponent,
  SlimButtonComponent,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { PublicCheckoutRecurrencePaymentComponent } from '../public-checkout-recurrence-payment/public-checkout-recurrence-payment.component';
import { HttpErrorService, NavigatorService } from '@web/common';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SummernoteHtmlPreviewComponent } from '@web/shared/external';

@Component({
  selector: 'app-public-order-payment',
  imports: [
    AlertsComponent,
    CommonModule,
    CurrencyComponent,
    PublicCheckoutRecurrencePaymentComponent,
    SpinnerFilledComponent,
    SlimButtonComponent,
    OrderStatusPipe,
    SummernoteHtmlPreviewComponent,
  ],
  templateUrl: './public-order-payment.component.html',
  styleUrl: './public-order-payment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class PublicOrderPaymentComponent implements OnInit {
  private readonly orderDataSource = inject(PublicOrderDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);
  private readonly navigator = inject(NavigatorService);

  public readonly accessToken = input.required<string>();
  public readonly orderId = input.required<Guid>();
  public readonly redirect = input<boolean>(false);

  protected readonly seconds = signal(10);
  protected readonly data = signal<Nillable<PublicCheckoutOrderDetails>>(null);
  /* prettier-ignore */
  protected readonly shouldShowPayForTheRecurringOrder = this.shouldShowPayForTheRecurringOrderSignal();
  /* prettier-ignore */
  protected readonly shouldShowPayForTheOrder = this.shouldShowPayForTheOrderSignal();
  protected readonly shouldShowPaymentMethodDescription =
    this.shouldShowPaymentMethodDescriptionSignal();
  protected readonly valueOrDefault = valueOrDefault;

  private readonly destroyRef = inject(DestroyRef);
  private readonly timerStop$ = new Subject<void>();

  public ngOnInit(): void {
    this.fetchCheckoutOrderDetails();
  }

  public handlePaymentClick(): void {
    this.timerStop$.next();

    this.tryToRedirectToPaymentSystemService();
  }

  public handleTokenizeClick(token: string): void {
    this.processOrderWithSecureToken(token);
  }

  private tryToRedirectToPaymentSystemService(): void {
    const redirectUrl = this.data()?.redirectUrl;

    if (isNil(redirectUrl)) {
      return;
    }

    this.redirectToPaymentSystem(redirectUrl);
  }

  private shouldShowPayForTheRecurringOrderSignal(): Signal<boolean> {
    return computed(() => {
      const data = this.data();

      if (isNil(data)) {
        return false;
      }

      return data.paymentMethod.isPayURecursive() && data.isNew();
    });
  }

  private shouldShowPayForTheOrderSignal(): Signal<boolean> {
    return computed(() => {
      const data = this.data();

      if (isNil(data)) {
        return false;
      }

      return (
        !this.shouldShowPayForTheRecurringOrder() &&
        data.isPending() &&
        data.paymentMethod.isBankTransfer()
      );
    });
  }

  private shouldShowPaymentMethodDescriptionSignal(): Signal<boolean> {
    return computed(() => {
      const data = this.data();

      if (isNil(data)) {
        return false;
      }

      return data.paymentMethod.isBankTransfer();
    });
  }

  private processOrderWithSecureToken(token: string): void {
    this.orderDataSource
      .process({
        accessToken: this.accessToken(),
        orderId: this.orderId(),
        secureFormToken: token,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private startTimer(): void {
    interval(1000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        takeUntil(this.timerStop$),
        takeWhile(() => this.seconds() > 0),
      )
      .subscribe(() => {
        this.seconds.update((seconds) => seconds - 1);

        if (this.seconds() === 0) {
          this.timerStop$.next();
          this.tryToRedirectToPaymentSystemService();
        }
      });
  }

  private fetchCheckoutOrderDetails(): void {
    this.orderDataSource
      .fetchCheckoutOrderDetailsById({
        accessToken: this.accessToken(),
        orderId: this.orderId(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((details) => {
        this.data.set(details);

        if (this.shouldStartTimer(details)) {
          this.startTimer();
        }
      });
  }

  private shouldStartTimer(details: PublicCheckoutOrderDetails): boolean {
    return (
      this.redirect() &&
      !details.paymentMethod.isPayURecursive() &&
      !details.paymentMethod.isBankTransfer()
    );
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }

  private redirectToPaymentSystem(redirectUrl: string): void {
    this.navigator.navigateByExternalLink(redirectUrl);
  }
}
